import { CssBaseline, StyledEngineProvider, ThemeProvider } from "@mui/material";
import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import { createContext, useContext, useMemo } from "react";
import { initReactI18next, useTranslation } from 'react-i18next';
import { format, getDirectionByLanguage, verifyNonNull } from "../Helpers/Utils";
import getLanguageProvider from "./Languages";
import theme from "./Theme";
import "./index.css"
import { CacheProvider } from "@emotion/react";
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from '@mui/stylis-plugin-rtl';
import { getStandardMicrospecCookie, setStandardMicrospecCookie } from "../Helpers/LocalPersistenceUtils";

// const cssToMuiLoader = cssToMui("./assets/css/style.css")
const MicrospecThemeLanguageContext = createContext()

const languageLocalStorageKey = 'language'

const MicrospecThemeProvider = ({ children, config }) => {
    const localization = useMemo(() => handleLocalization(config.languages), [config.languages])
    const { t } = useTranslation()
    const { dir, choosen, internalLanguageProvider } = localization
    const cacheRtl = useMemo(() => {
        if (dir === 'rtl') {
            return createCache({
                key: 'muirtl',
                stylisPlugins: [
                    prefixer,
                    rtlPlugin
                ],
            })
        }
    }, [dir])

    const translateByType = (obj, ...args) => {
        if (typeof obj === "string") {
            return format(t(obj), ...args);
        }
        if (!obj || !obj.text) {
            return null;
        }
        let value;
        switch (obj.type) {
            case "Localized":
                value = t(obj.text);
                break;
            case "Internal":
                value = internalLanguageProvider(obj.text, obj.lng)
                break;
            default:
                value = obj.text
                break;
        }
        return format(value, ...args)
    }

    const translateByMultiLangObject = (multiLangObj, ...args) => {
        if (!multiLangObj || typeof multiLangObj !== 'object') {
            return null;
        }
        let value = multiLangObj[choosen];
        if (!value) {
            value = multiLangObj[config.languages.default];
        }
        if (!value) {
            value = Object.values(multiLangObj)[0];
        }
        return format(value, ...args)
    }

    const translate = (obj, ...args) => {
        if (typeof obj === "object" && !obj.text && !obj.type && Object.keys(obj).length > 0) {
            return translateByMultiLangObject(obj, ...args)
        }
        return translateByType(obj, ...args)
    }

    const localizationProvider = { 
        dir, 
        choosen,
        supportedLanguagesMap: config.languages.list.reduce((acc, cur) => {
            acc[cur.code] = cur.name
            return acc
        }, {}),
        translateByType, 
        translateByMultiLangObject, 
        translate, 
    }

    return (
        <MicrospecThemeLanguageContext.Provider value={{ config, localizationProvider }}>
            <CacheProvider value={cacheRtl}>
                <ThemeProvider theme={theme(config, choosen)}>
                    <StyledEngineProvider injectFirst>
                        <CssBaseline />
                        {children}
                    </StyledEngineProvider>
                </ThemeProvider>
            </CacheProvider>
        </MicrospecThemeLanguageContext.Provider>
    )
}

const initI18nIfNeeded = (defaultLanguage, choosenLanguage) => {
    if (!i18n.isInitialized) {
        // Init i18n synchronously
        i18n
            .use(Backend)
            .use(initReactI18next)
            .init({
                lng: choosenLanguage,
                fallbackLng: defaultLanguage,
                debug: false,
                useSuspense: true,
                backend: {
                    loadPath: '/locales/{{lng}}/{{ns}}.json'
                }
            });
    }
}

const handleLocalization = (languages) => {
    verifyLanguages(languages)
    const choosen = handleLanguageLocalStorageFlow(languages)
    initI18nIfNeeded(languages.default, choosen)
    const dir = handleDirectionByLanguage(languages.choosen)
    const internalLanguageProvider = (key, lng) => {
        const provider = getLanguageProvider(lng || choosen)
        const value = provider[key]
        return value ? value : key
    }
    return { choosen, dir, internalLanguageProvider }
}
//
const verifyLanguages = (languages) => {
    verifyNonNull(languages, 'languages')
    verifyNonNull(languages.default, 'languages.default')
    verifyNonNull(languages.list, 'languages.list')
    languages.codes = languages.list.map((v) => {
        verifyNonNull(v.code)
        verifyNonNull(v.name)
        return v.code
    })//
}

const handleLanguageLocalStorageFlow = (languages) => {
    let localized = getStandardMicrospecCookie(languageLocalStorageKey);
    if (!localized || !languages.codes.includes(localized)) {
        localized = languages.default;
    }
    const choosen = localized;
    setStandardMicrospecCookie(languageLocalStorageKey, choosen)
    languages.choosen = choosen
    return choosen
}

const handleDirectionByLanguage = (choosen) => {
    const dir = getDirectionByLanguage(choosen)
    if (dir !== document.documentElement.getAttribute('dir')) {
        document.documentElement.setAttribute('dir', dir);
        document.documentElement.setAttribute('lang', choosen)
    }
    return dir
}

export const handleLanguageChange = (choosen) => {
    setStandardMicrospecCookie(languageLocalStorageKey, choosen)
    window.location.reload()
}

export const handleThemeModeChange = () => {
    const currentMode = getStandardMicrospecCookie('mode') || 'light';
    const newMode = currentMode === 'light' ? 'dark' : 'light';
    setStandardMicrospecCookie('mode', newMode);
    window.location.reload();
}

export const useMicrospecThemeLanguageContext = () => useContext(MicrospecThemeLanguageContext)
export default MicrospecThemeProvider